import type { FC, SVGProps } from "react"
import { useEffect, useState } from "react"
import * as cheerio from "cheerio"
import { format } from "date-fns"
import {
	IconAtom2Fill18,
	IconBoltFill18,
	IconChatsFill18,
	IconCode2Fill18,
	IconEnvelopeFill18,
	IconFileContentFill18,
	IconHeartHandshakeFill18,
	IconMessageFill18,
	IconSquareChevronUpFill18,
	IconVideoFill18,
} from "nucleo-ui-fill-18"
import * as R from "remeda"

import type { FeedbackOrigin } from "@productlane/db/browser"
import type { IconType } from "@productlane/ui"
import type { Feedback } from "@productlane/zero"
import type { TeamMember } from "@productlane/zero/queries"
import { feedbackOriginText } from "@productlane/lib"
import {
	FrontIcon,
	HubspotIcon,
	IntercomIcon,
	LinearIcon,
	PlainLogo,
	SlackIcon,
	ZapierIcon,
	ZendeskIcon,
} from "@productlane/ui"
import { TeamsIcon } from "@productlane/ui/src/icons/project/ms-teams-icon"

import { RequestsIcon } from "@/screens/app/nav-icons"

export const FeedbackOriginIconRecord: Record<
	FeedbackOrigin,
	IconType | LucideIcon
> = {
	API: IconCode2Fill18,
	INAPP: IconFileContentFill18,
	PORTAL: RequestsIcon,
	SUPPORT_PORTAL: IconHeartHandshakeFill18,
	EMAIL: IconEnvelopeFill18,
	SLACK: SlackIcon,
	SLACK_CONNECT: SlackIcon,
	INTERCOM: IntercomIcon,
	INTERCOM_ATTACHMENT: IntercomIcon,
	FRONT_ATTACHMENT: FrontIcon,
	ZAPIER: ZapierIcon,
	ZENDESK: ZendeskIcon,
	ZENDESK_ATTACHMENT: ZendeskIcon,
	HUBSPOT: HubspotIcon,
	PLAIN: PlainLogo,
	CALENDAR: IconVideoFill18,
	WIDGET: IconBoltFill18,
	AI_CHAT: IconAtom2Fill18,
	UPVOTE: IconSquareChevronUpFill18,
	LIVE_CHAT: IconChatsFill18,
	TEAMS: TeamsIcon,
	CUSTOM: IconMessageFill18,
	LINEAR: LinearIcon,
}

export function getGrainHighlightIdsFromFeedbacks(args: {
	texts: Array<string>
	highlightId: string
}) {
	let grainIds: Array<string> = []
	for (const i of args.texts) {
		const $ = cheerio.load(i, { decodeEntities: false })

		const links = $(`mark[data-id=${args.highlightId}]`).parent("a")

		links.each((_, value) => {
			const href = $(value).attr("href")
			if (href?.includes("grain.com/share/highlight")) {
				const id = href.split("/").pop()
				if (id) {
					grainIds.push(id)
				}
			}
		})
	}
	grainIds = R.uniq(grainIds)
	return grainIds
}

export function useOriginText({
	thread,
	teamMembers,
}: {
	thread?: Feedback
	teamMembers?: readonly TeamMember[]
}) {
	const [originText, setOriginText] = useState("")

	useEffect(() => {
		if (thread) {
			const author = teamMembers
				? teamMembers.find((c) => c.userId === thread?.reporterId)
				: null
			const formattedTime = format(thread.createdAt, "EEE, MMM d 'at' h:mm a")

			if (thread.origin === "INAPP") {
				setOriginText(
					`Created ${formattedTime} ${
						author ? `by ${author?.user?.name}` : ""
					}`,
				)
				return
			}

			const text = feedbackOriginText(thread.origin, thread.customChannel)
			setOriginText(
				`Created ${formattedTime} ${text}${
					author ? ` by ${author?.user?.name}` : ""
				}`,
			)
		} else {
			setOriginText("")
		}
	}, [teamMembers, thread])

	return { originText, teamMembers }
}

type LucideIcon = FC<SVGProps<SVGSVGElement>>
